<template>
  <div class="container mx-auto m-5">
    <div class="flex flex-wrap gap-3 justify-end mb-5">
      <PrimaryButton text="Add service" @click="$router.push('/calculator')" />
      <PrimaryButton text="Create group" @click="showCreateGroupModal" />
      <PrimaryButton text="Add to group" @click="showMoveToModal" />
      <PrimaryButton text="Remove" @click="showRemoveModal" />
    </div>

    <div class="bg-white shadow-lg rounded-sm border border-slate-200">
      <div
        class="p-5 text-lg bg-[#F8FAFC] font-semibold border-b border-slate-200 flex items-center justify-between"
      >
        <span>Services summary</span>
        <span class="text-sm font-normal text-slate-500">
          {{ addedServices.length }} {{ addedServices.length === 1 ? "service" : "services" }}
        </span>
      </div>

      <div class="p-5">
        <div v-if="addedServices.length === 0" class="text-center py-8 text-slate-500">
          <p>No services added yet</p>
          <router-link to="/calculator" class="text-indigo-500 hover:underline mt-2 inline-block">
            Add your first service
          </router-link>
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="service in addedServices"
            :key="service.id"
            class="border border-slate-200 rounded hover:bg-slate-50 transition-colors"
          >
            <div class="flex items-start gap-4 p-4">
              <input
                type="checkbox"
                :checked="isSelected(service.id)"
                class="mt-1 w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                @change="toggleSelected(service.id)"
              />

              <div class="flex-1 min-w-0">
                <div class="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-2">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <h4 class="font-semibold text-slate-800">{{ service.name }}</h4>
                      <span
                        v-if="service.onRequest"
                        class="px-2 py-0.5 text-[10px] font-medium rounded bg-indigo-100 text-indigo-700"
                      >
                        On request
                      </span>
                      <span
                        v-if="service.dynamic === 'support'"
                        class="px-2 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700"
                      >
                        Follows your estimate
                      </span>
                    </div>
                    <p class="text-sm text-slate-600 mt-1">
                      {{ service.description || configSummary(service) }}
                    </p>
                  </div>

                  <div class="flex items-center gap-4 shrink-0">
                    <div v-if="canChangeQuantity(service)" class="flex items-center gap-2">
                      <span class="text-xs text-slate-500">Qty</span>
                      <div class="flex items-center border border-slate-300 rounded">
                        <button
                          class="px-2 py-1 text-slate-600 hover:bg-slate-100"
                          @click="setQuantity(service, service.quantity - 1)"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="1"
                          class="w-14 text-center border-0 focus:ring-0 text-sm py-1"
                          :value="service.quantity"
                          @change="setQuantity(service, $event.target.value)"
                        />
                        <button
                          class="px-2 py-1 text-slate-600 hover:bg-slate-100"
                          @click="setQuantity(service, service.quantity + 1)"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div class="text-right">
                      <p class="font-semibold text-slate-800 w-max">
                        <span v-if="service.onRequest" class="text-sm text-indigo-600">
                          On request
                        </span>
                        <span v-else v-currency="service.monthlyCost" :key="service.monthlyCost">
                          {{ service.monthlyCost }}
                        </span>
                        <span v-if="!service.onRequest" class="text-sm font-normal text-slate-500">
                          /month
                        </span>
                      </p>
                      <p v-if="!service.onRequest" class="text-xs text-slate-500">
                        <span v-currency="service.totalCost" :key="service.totalCost">
                          {{ service.totalCost }}
                        </span>
                        over 12 months
                      </p>
                    </div>
                  </div>
                </div>

                <div class="flex flex-wrap items-center gap-4 text-xs">
                  <button
                    class="text-indigo-600 hover:underline"
                    @click="toggleExpanded(service.id)"
                  >
                    {{ isExpanded(service.id) ? "Hide" : "Show" }} calculations
                  </button>
                  <button class="text-slate-600 hover:underline" @click="edit(service)">
                    Edit configuration
                  </button>
                  <button class="text-slate-600 hover:underline" @click="duplicate(service)">
                    Duplicate
                  </button>
                  <button class="text-red-600 hover:underline" @click="remove(service)">
                    Remove
                  </button>
                </div>

                <div v-if="isExpanded(service.id)" class="bg-slate-50 rounded mt-3">
                  <div v-if="service.items && service.items.length">
                    <p class="text-xs font-medium text-slate-700 px-4 pt-3">
                      Per unit, per month
                      <span v-if="service.quantity > 1"> — × {{ service.quantity }} units</span>
                    </p>
                    <LineItems :items="service.items" />
                  </div>
                  <p v-else class="text-sm text-slate-600 p-4">
                    {{ configSummary(service) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / add to group modal -->
    <div
      v-if="modal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="modal = false"
    >
      <div class="bg-white rounded-sm shadow-lg max-w-md w-full mx-4 border border-slate-200">
        <div class="p-5">
          <h3 class="text-lg font-semibold text-slate-800 mb-4">
            {{ selectedIds.length }} selected
          </h3>

          <p v-if="!selectedIds.length" class="text-sm text-slate-600 mb-4">
            Select at least one service first.
          </p>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">New group name</label>
              <input
                class="form-input w-full"
                type="text"
                placeholder="Enter group name"
                v-model="groupName"
              />
            </div>

            <div v-if="groups.length" class="border-t pt-4">
              <h4 class="text-sm font-medium text-slate-700 mb-2">Or add to an existing group:</h4>
              <div class="space-y-2 max-h-32 overflow-y-auto">
                <button
                  v-for="group in groups"
                  :key="group.name"
                  @click="addServicesToGroup(group)"
                  class="w-full text-left p-2 text-sm border border-slate-200 rounded hover:bg-slate-50 transition-colors"
                >
                  {{ group.name }}
                </button>
              </div>
            </div>
          </div>

          <div class="flex gap-3 justify-end mt-6">
            <button
              @click="modal = false"
              class="px-3 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              @click="createGroup"
              class="px-3 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Remove modal -->
    <div
      v-if="removeModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="removeModal = false"
    >
      <div class="bg-white rounded-sm shadow-lg max-w-md w-full mx-4 border border-slate-200">
        <div class="p-5">
          <h3 class="text-lg font-semibold text-slate-800 mb-4">Remove services</h3>
          <p class="text-sm text-slate-600 mb-6">
            Remove {{ selectedIds.length }} selected
            {{ selectedIds.length === 1 ? "service" : "services" }} from your estimate and groups?
          </p>
          <div class="flex gap-3 justify-end">
            <button
              @click="removeModal = false"
              class="px-3 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              @click="removeSelected"
              class="px-3 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PrimaryButton from "./PrimaryButton.vue";
import LineItems from "@/components/config/LineItems.vue";
import { generateConfigSummary } from "@/utils/configSummary";

export default {
  props: {
    addedServices: { type: Array, default: () => [] },
  },
  components: {
    PrimaryButton,
    LineItems,
  },
  data() {
    return {
      modal: false,
      groupName: "",
      removeModal: false,
      selectedIds: [],
      expandedIds: [],
    };
  },
  computed: {
    groups() {
      return this.$store.getters["services/groups"];
    },
  },
  methods: {
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    toggleSelected(id) {
      this.selectedIds = this.isSelected(id)
        ? this.selectedIds.filter((entry) => entry !== id)
        : [...this.selectedIds, id];
    },
    isExpanded(id) {
      return this.expandedIds.includes(id);
    },
    toggleExpanded(id) {
      this.expandedIds = this.isExpanded(id)
        ? this.expandedIds.filter((entry) => entry !== id)
        : [...this.expandedIds, id];
    },

    // Support lines are priced off the rest of the estimate, so a quantity
    // would be meaningless there.
    canChangeQuantity(service) {
      return !service.onRequest && !service.dynamic;
    },
    setQuantity(service, quantity) {
      const next = Math.max(1, Math.floor(Number(quantity) || 1));
      this.$store.commit("services/SET_SERVICE_QUANTITY", {
        id: service.id,
        quantity: next,
      });
    },
    duplicate(service) {
      this.$store.commit("services/DUPLICATE_SERVICE", service.id);
    },
    remove(service) {
      this.$store.commit("services/REMOVE_SERVICE", service);
      this.selectedIds = this.selectedIds.filter((id) => id !== service.id);
    },
    edit(service) {
      if (!service.serviceId) return;
      this.$router.push(`/config/services/${service.serviceId}`);
    },

    showCreateGroupModal() {
      this.modal = true;
    },
    showMoveToModal() {
      this.modal = true;
    },
    createGroup() {
      if (this.groupName.trim() === "") return;

      this.$store.commit("services/GROUPS", {
        name: this.groupName.trim(),
        services: this.selectedServices().map((service) => ({
          id: service.id,
          name: service.name,
        })),
        showServices: false,
      });
      this.groupName = "";
      this.modal = false;
    },
    addServicesToGroup(group) {
      this.selectedServices().forEach((service) => {
        this.$store.commit("services/ADD_SERVICE_TO_GROUP", { group, service });
      });
      this.modal = false;
    },
    showRemoveModal() {
      if (!this.selectedIds.length) return;
      this.removeModal = true;
    },
    removeSelected() {
      this.selectedServices().forEach((service) => {
        this.$store.commit("services/REMOVE_SERVICE", service);
      });
      this.selectedIds = [];
      this.removeModal = false;
    },
    selectedServices() {
      return this.addedServices.filter((service) => this.isSelected(service.id));
    },
    configSummary(service) {
      return generateConfigSummary(service);
    },
  },
};
</script>

<style lang="postcss" scoped></style>
